import { Badge, deepMerge } from '@mantine/core';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HarvestProofReminderFilter } from '../../components/harvest-proof-reminder-filter/harvest-proof-reminder-filter';
import { HarvestProofTcSmsFilter } from '../../components/harvest-proof-tc-sms-filter/harvest-proof-tc-sms-filter';
import { HarvestProofRemindersStatus } from '../../components/harvest-proof-reminders-status/harvest-proof-reminders-status';
import { HarvestProofStatusBadge } from '../../components/harvest-proof-status-badge/harvest-proof-status-badge';
import { HarvestProofStatusFilter } from '../../components/harvest-proof-status-filter/harvest-proof-status-filter';
import { HarvestProofDetailPanel } from '../harvest-proof-detail-panel/harvest-proof-detail-panel';

import { useFiltersTableOptions } from '@data-access';
import {
  HarvestProofFormListItem,
  HarvestProofFormStatus,
  HarvestProofReminderFilterValue,
  useHarvestProofList,
} from '@harvest-proof/data-access';
import { PfTable } from '@ui';

export function HarvestProofTable() {
  const { t } = useTranslation();
  const filtersTableOptions =
    useFiltersTableOptions<HarvestProofFormListItem>('harvestProof');
  const listQuery = useHarvestProofList();

  const columns = useMemo<MRT_ColumnDef<HarvestProofFormListItem>[]>(
    () => [
      {
        accessorKey: 'account_name',
        header: t('harvest-proof.columns.account'),
      },
      {
        accessorKey: 'organization_name',
        header: t('harvest-proof.columns.organization'),
      },
      {
        accessorKey: 'account_phone',
        header: t('harvest-proof.columns.phone'),
      },
      {
        accessorKey: 'is_tc_sms',
        header: t('harvest-proof.columns.tcSms'),
        enableSorting: false,
        size: 120,
        Cell: ({ cell }) => (
          <Badge
            color={cell.getValue<boolean>() ? 'teal' : 'gray'}
            variant="light"
          >
            {cell.getValue<boolean>() ? t('common.yes') : t('common.no')}
          </Badge>
        ),
        Filter: ({ column }) => (
          <HarvestProofTcSmsFilter
            onChange={(value) => column.setFilterValue(value)}
            onClear={() => column.setFilterValue(null)}
            value={column.getFilterValue() as 'true' | 'false' | undefined}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: t('harvest-proof.columns.status'),
        Cell: ({ cell }) => (
          <HarvestProofStatusBadge
            status={cell.getValue<HarvestProofFormStatus>()}
            variant="form"
          />
        ),
        Filter: ({ column }) => (
          <HarvestProofStatusFilter
            onChange={(value) => column.setFilterValue(value)}
            onClear={() => column.setFilterValue(null)}
            value={column.getFilterValue() as HarvestProofFormStatus | undefined}
          />
        ),
      },
      {
        id: 'reminder',
        accessorKey: 'reminder1_sent',
        header: t('harvest-proof.columns.reminders'),
        enableSorting: false,
        enableColumnFilter: true,
        size: 180,
        Cell: ({ row }) => <HarvestProofRemindersStatus form={row.original} />,
        Filter: ({ column }) => (
          <HarvestProofReminderFilter
            onChange={(value) => column.setFilterValue(value)}
            onClear={() => column.setFilterValue(null)}
            value={column.getFilterValue() as HarvestProofReminderFilterValue | undefined}
          />
        ),
      },
      {
        accessorKey: 'completion_percent',
        header: t('harvest-proof.columns.completion'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        Cell: ({ cell }) => `${cell.getValue<number>()} %`,
      },
      {
        accessorKey: 'answers_count',
        header: t('harvest-proof.columns.parcels'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
        Cell: ({ row }) =>
          `${row.original.finished_count} / ${row.original.answers_count}`,
      },
    ],
    [t],
  );

  const tableOptions: MRT_TableOptions<HarvestProofFormListItem> = deepMerge(
    {
      columns,
      data: listQuery.data?.results ?? [],
      rowCount: listQuery.data?.count ?? 0,
      enableRowActions: false,
      enableExpanding: true,
      enableExpandAll: false,
      positionExpandColumn: 'first',
      renderDetailPanel: ({ row }) => (
        <HarvestProofDetailPanel formId={row.original.id} />
      ),
      mantineTableBodyRowProps: ({ row, isDetailPanel }) =>
        isDetailPanel
          ? {}
          : {
              onClick: () => row.toggleExpanded(),
              style: { cursor: 'pointer' },
            },
      mantinePaperProps: {
        h: '100%',
        withBorder: false,
        shadow: 'none',
        p: 0,
      },
      initialState: {
        showColumnFilters: true,
      },
      state: {
        showLoadingOverlay: listQuery.isLoading,
        showProgressBars: listQuery.isFetching,
      },
    },
    filtersTableOptions,
  );

  return <PfTable<HarvestProofFormListItem> options={tableOptions} />;
}
